import { useEffect } from "react";
import { UserAPI } from "../../../shared/types";
import { useAuth } from "./platform/AuthProvider";
import { api } from "./api";
import posthog from "posthog-js";

export interface Rewardful {
  referral?: string;
}

export function getRewardfulClient() {
  return (window as any).Rewardful as Rewardful;
}

export function tiktokEvent(event: string, data?: any) {
  (window as any).ttq?.track?.(event, data);
}

export function gtagEvent(event: string, data?: any) {
  (window as any).gtag?.("event", event, data);
}

export function fbqEvent(event: string, data?: any) {
  (window as any).fbq?.("track", event, data);
}

export function gtmEvent(event: string, data?: Record<string, any>) {
  const dataLayer = (window as any).dataLayer;
  if (dataLayer) {
    dataLayer.push({
      event,
      ...data,
    });
  }
}
export function posthogEvent(event: string, data?: Record<string, any>) {
  posthog.capture(event, data);
}

// Generate a unique event ID for deduplication
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

class Tracking {
  userData:
    | {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
      }
    | undefined = undefined;

  /**
   * Track an event - sends to both client-side GTM and server
   */
  private trackEvent(eventName: string, eventParams?: Record<string, any>) {
    const eventId = generateEventId();

    // Push to client-side GTM immediately (for real-time analytics)
    gtmEvent(eventName, {
      ...eventParams,
      event_id: eventId,
      user_data: this.userData,
    });
    posthogEvent(eventName, {
      ...eventParams,
      distinctId: eventId,
      userData: this.userData,
    });
  }

  initialized() {
    this.trackEvent("auth_initialized");
    if (this.userData) {
      posthog.identify(this.userData?.id, {
        email: this.userData?.email,
        first_name: this.userData?.first_name,
        last_name: this.userData?.last_name,
      });
    }
  }

  trackPage(page: string) {
    this.trackEvent("page_view", {
      page_title: page,
      page_location: window.location.href,
    });
  }

  trackCalendarLinkClicked(provider: string) {
    this.trackEvent("calendar_link_clicked", {
      provider,
    });
  }

  landingHeadFirst() {
    this.trackEvent("head_first");
  }

  landingHeartFirst() {
    this.trackEvent("heart_first");
  }

  quizStarted() {
    this.trackEvent("quiz_started");
  }

  quizCompleted() {
    this.trackEvent("quiz_completed");
  }

  initiateCheckout() {
    this.trackEvent("initiate_checkout");
  }

  pollVote(pollId: string) {
    this.trackEvent("poll_vote", {
      poll_id: pollId,
    });
  }

  purchase(paymentOption: UserAPI.PaymentOption, purchaseId: string) {
    this.trackEvent("purchase", {
      currency: paymentOption.currency ?? "USD",
      value: paymentOption.price / 100,
      transaction_id: purchaseId,
      coupon: paymentOption.couponId,
      item_name: paymentOption?.name,
      item_id: paymentOption?.id,
    });
  }
}

const tracking = new Tracking();

export default tracking;

export function TrackingInitializer({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user: authUser, isLoaded: isAuthLoaded } = useAuth();
  useEffect(() => {
    installServerSideTracking();
    if (authUser) {
      tracking.userData = {
        id: authUser?.id,
        email: authUser?.email ?? "",
        first_name: authUser?.firstName ?? "",
        last_name: authUser?.lastName ?? "",
      };
    } else {
      tracking.userData = undefined;
    }
    if (isAuthLoaded) {
      tracking.initialized();
    }
  }, [isAuthLoaded, authUser]);
  return children;
}

const installServerSideTracking = () => {
  const serverTracker = (data: any) => {
    const ENDPOINT = '/tracking/events';
    try {
      api.post(ENDPOINT, {events: [data]});
      return true;
    } catch (e) {
      console.error('Tracker Failed:', e);
      return false;
    }
  };
  (window as any).serverTracker = serverTracker;
}